import React, { useState, useEffect, useRef, useCallback} from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Link,
  TextField
} from '@mui/material';
import { Code as CodeIcon, Link as LinkIcon } from '@mui/icons-material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { Accordion, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import useContentActions from '../../hooks/useContentActions';
import useIntegrationVersions from '../../hooks/useIntegrationVersions';
import useExplain from '../../hooks/useExplain';
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import LoadingBox from '../atoms/LoadingBox';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import InlineImproveInput from '../common/InlineImproveInput';
import AddSectionModal from '../atoms/AddSectionModal';
import conversationService from '../../services/conversationService';
import useImprove from '../../hooks/useImprove';
import useParsedSections from '../../hooks/useParsedSections';
import {
  formatAuthType,
  getAuthTypeBadgeColor,
  formatIntegrationForCopy,
  reconstructIntegrationMarkdown,
  extractIntegrations,
  parseIntegrationDetails
} from '../../utils/integrationParser';

// Helper function to detect auth type (inline for component use)
const detectAuthType = (authString) => {
  if (!authString) return null;
  const authLower = authString.toLowerCase();
  if (authLower.includes('oauth')) return 'oauth';
  if (authLower.includes('bearer') || authLower.includes('token')) return 'bearer';
  if (authLower.includes('api key')) return 'apikey';
  if (authLower.includes('basic')) return 'basic';
  return null;
};

// Helper to render text with basic markdown (bold, italic)
const renderTextWithMarkdown = (text) => {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;

  // Match **bold** and *italic* patterns
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const matchedText = match[0];
    if (matchedText.startsWith('**')) {
      // Bold text
      parts.push(<strong key={match.index}>{matchedText.slice(2, -2)}</strong>);
    } else {
      // Italic text
      parts.push(<em key={match.index}>{matchedText.slice(1, -1)}</em>);
    }

    lastIndex = match.index + matchedText.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

// Helper function to format raw content with proper structure
const formatRawContent = (content) => {
  if (!content) return null;

  // Check if content is mostly plain text (no complex markdown)
  const hasComplexFormatting = content.includes('###') ||
                                content.includes('```') ||
                                content.match(/^\d+\.\s/m) ||
                                content.match(/^[-*]\s/m);

  // If it's simple text, preserve it exactly as-is
  if (!hasComplexFormatting) {
    return (
      <Typography
        component="div"
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.8,
          fontSize: '13px',
          wordBreak: 'break-word'
        }}
      >
        {content}
      </Typography>
    );
  }

  // Split by lines for complex formatting
  const lines = content.split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;

  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ul') {
        elements.push(
          <ul key={`list-${elements.length}`} style={{ marginLeft: '20px', marginBottom: '12px' }}>
            {currentList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: '6px', lineHeight: '1.6', wordBreak: 'break-word' }}>{item}</li>
            ))}
          </ul>
        );
      } else if (listType === 'ol') {
        elements.push(
          <ol key={`list-${elements.length}`} style={{ marginLeft: '20px', marginBottom: '12px' }}>
            {currentList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: '6px', lineHeight: '1.6', wordBreak: 'break-word' }}>{item}</li>
            ))}
          </ol>
        );
      } else if (listType === 'checklist') {
        elements.push(
          <Box key={`checklist-${elements.length}`} sx={{ ml: 1, mb: 1.5 }}>
            {currentList.map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
                <Box sx={{
                  minWidth: 16,
                  height: 16,
                  border: '2px solid #999',
                  borderRadius: '3px',
                  mt: '2px',
                  backgroundColor: item.checked ? '#4caf50' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {item.checked && <span style={{ color: 'white', fontSize: '10px' }}>✓</span>}
                </Box>
                <span style={{ flex: 1, lineHeight: '1.6', wordBreak: 'break-word' }}>{item.text}</span>
              </Box>
            ))}
          </Box>
        );
      } else if (listType === 'diagram') {
        // Handle ASCII diagrams/code blocks
        elements.push(
          <Box key={`diagram-${elements.length}`} sx={{
            backgroundColor: '#f8f9fa',
            p: 2,
            borderRadius: 1,
            mb: 2,
            overflow: 'auto',
            maxWidth: '100%'
          }}>
            <pre style={{
              margin: 0,
              fontSize: '11px',
              fontFamily: 'monospace',
              lineHeight: '1.4',
              whiteSpace: 'pre',
              overflow: 'auto'
            }}>
              {currentList.join('\n')}
            </pre>
          </Box>
        );
      }
      currentList = [];
      listType = null;
    }
  };

  let inCodeBlock = false;

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Handle code block markers
    if (trimmedLine.startsWith('```')) {
      if (!inCodeBlock) {
        flushList();
        listType = 'diagram';
        inCodeBlock = true;
      } else {
        flushList();
        inCodeBlock = false;
      }
      return;
    }

    // If in code block, add to diagram
    if (inCodeBlock) {
      if (listType !== 'diagram') {
        flushList();
        listType = 'diagram';
      }
      currentList.push(line); // Keep original formatting with spaces
      return;
    }

    // Detect ASCII diagrams (lines with box drawing characters or multiple special chars)
    const diagramChars = /[│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌]/;
    const hasMultipleSpecialChars = (line.match(/[+\-|\/\\]/g) || []).length > 3;
    if (diagramChars.test(line) || hasMultipleSpecialChars) {
      if (listType !== 'diagram') {
        flushList();
        listType = 'diagram';
      }
      currentList.push(line);
      return;
    }

    // Handle empty lines - preserve them for better spacing
    if (!trimmedLine) {
      flushList();
      // Always push a line break to preserve empty lines
      elements.push(<br key={`br-${index}`} />);
      return;
    }

    // Handle headers
    if (trimmedLine.startsWith('###')) {
      flushList();
      const text = trimmedLine.replace(/^###\s*/, '');
      elements.push(
        <Typography key={index} variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          {text}
        </Typography>
      );
      return;
    }

    if (trimmedLine.startsWith('##')) {
      flushList();
      const text = trimmedLine.replace(/^##\s*/, '');
      elements.push(
        <Typography key={index} variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          {text}
        </Typography>
      );
      return;
    }

    // Handle bold text patterns
    if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
      flushList();
      const match = trimmedLine.match(/\*\*(.+?)\*\*:?\s*(.*)/);
      if (match) {
        elements.push(
          <Box key={index} sx={{ mb: 1 }}>
            <strong>{match[1]}:</strong> {match[2]}
          </Box>
        );
        return;
      }
    }

    // Handle checklist items: - [ ] or - [x]
    const checklistMatch = trimmedLine.match(/^-\s*\[([x\s])\]\s*(.+)/i);
    if (checklistMatch) {
      if (listType !== 'checklist') {
        flushList();
        listType = 'checklist';
      }
      currentList.push({
        checked: checklistMatch[1].toLowerCase() === 'x',
        text: checklistMatch[2]
      });
      return;
    }

    // Handle unordered lists: - item or * item
    if (trimmedLine.match(/^[-*]\s+(.+)/)) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(trimmedLine.replace(/^[-*]\s+/, ''));
      return;
    }

    // Handle numbered lists: 1. item, 2. item, etc.
    // But exclude if it looks like a main section header (ends with colon and has section-like title)
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)/);
    if (numberedMatch) {
      const itemText = numberedMatch[1];
      const endsWithColon = itemText.trim().endsWith(':');

      // If it ends with colon, it's likely a sub-heading within content, render as bold text
      if (endsWithColon) {
        flushList();
        elements.push(
          <Typography key={index} variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
            {trimmedLine}
          </Typography>
        );
        return;
      }

      // Otherwise it's a numbered list item
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(itemText);
      return;
    }

    // Regular text
    flushList();
    elements.push(
      <Typography key={index} variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
        {trimmedLine}
      </Typography>
    );
  });

  flushList(); // Flush any remaining list

  return <Box>{elements}</Box>;
};

const SplitViewIntegration = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [expandedIntegrations, setExpandedIntegrations] = useState({});
  const [visibleIntegrations, setVisibleIntegrations] = useState(10);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, integration: null });
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);

  const { showNotification } = useNotification();
  const integrationPanelRef = useRef(null);
  const isLocalUpdateRef = useRef(false);
  const improveInputRefs = useRef({}); // Store refs for each improve input

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedIntegrations,
    setSections: setParsedIntegrations,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // Use version management for integrations
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useIntegrationVersions(conversation, isStreaming, streamingContent);

  // Track previous version index
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible integrations when switching versions
  useEffect(() => {
    if (currentVersion) {
      const versionChanged = prevVersionIndexRef.current !== currentVersionIndex;
      if (versionChanged) {
        setVisibleIntegrations(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Initialize validation state for integrations with version-specific IDs
  const initialValidationState = parsedIntegrations.reduce((acc, integration) => {
    const versionSpecificId = `${integration.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook
  const {
    validatedItems: validatedIntegrations,
    handleValidateItem: handleValidateIntegrationBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'integration',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'integration',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateIntegration = async (integrationId) => {
    const versionSpecificId = `${integrationId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateIntegrationBase(versionSpecificId);
  };

  // Use full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainIntegration,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveIntegration,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    handleExport: handleExportIntegrations,
    getExportContent,
    handleCopy: handleCopyIntegrations
  } = useContentActions(contentRef, 'Integrations', projectId, conversation?._id);

  // Check if this is an integration conversation
  const isIntegrationConversation = conversation?.taskId === 'integration' ||
    (conversation?.messages?.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.toLowerCase().includes('oauth') ||
       msg.content?.toLowerCase().includes('api') ||
       msg.content?.toLowerCase().includes('integration'))
    ));

  const showIntegrationPanel = parsedIntegrations.length > 0 && isIntegrationConversation;

  const handleIntegrationToggle = (integrationId) => {
    setExpandedIntegrations(prev => ({
      ...prev,
      [integrationId]: !prev[integrationId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleIntegrations(prev => Math.min(prev + 10, parsedIntegrations.length));
  };

  const handleShowAll = () => {
    setVisibleIntegrations(parsedIntegrations.length);
  };

  // Helper function to reconstruct full editable content from parsed integration
  const reconstructEditableContent = (integration) => {
    let content = '';

    // Start with raw content if it exists
    if (integration.rawContent) {
      content = integration.rawContent;
    }

    // Always ensure Overview section is present
    const hasOverview = /##\s+Overview/i.test(content);
    if (!hasOverview && integration.description) {
      const overviewSection = `## Overview\n${integration.description}\n\n`;
      content = overviewSection + content;
    }

    // Check if "What We Are Going to Implement" section is missing
    const hasWhatToImplement = /##\s+What\s+We\s+Are\s+Going\s+to\s+Implement/i.test(content);
    if (integration.whatToImplement && integration.whatToImplement.length > 0 && !hasWhatToImplement) {
      // Insert after Overview
      const overviewMatch = content.match(/(##\s+Overview[\s\S]*?)(?=\n##|$)/i);
      const whatSection = `\n\n## What We Are Going to Implement\n${integration.whatToImplement.map(item => `- ${item}`).join('\n')}`;

      if (overviewMatch) {
        const endOfOverview = content.indexOf(overviewMatch[0]) + overviewMatch[0].length;
        content = content.slice(0, endOfOverview) + whatSection + content.slice(endOfOverview);
      } else {
        content = whatSection + '\n\n' + content;
      }
    }

    // Check if "Implementation Approaches" section is missing
    const hasImplementationApproaches = /##\s+Implementation\s+Approaches/i.test(content);
    if (integration.implementationApproaches && integration.implementationApproaches.length > 0 && !hasImplementationApproaches) {
      let approachesSection = '\n\n## Implementation Approaches\n';

      integration.implementationApproaches.forEach((approach, idx) => {
        approachesSection += `\n### Approach ${idx + 1}: ${approach.name}\n`;

        if (approach.explanation) {
          approachesSection += `**Explanation:** ${approach.explanation}\n\n`;
        }

        if (approach.codeRequirements && approach.codeRequirements.length > 0) {
          approachesSection += `**Code Requirements:**\n${approach.codeRequirements.map(req => `- ${req}`).join('\n')}\n\n`;
        }

        if (approach.advantages && approach.advantages.length > 0) {
          approachesSection += `**Advantages:**\n${approach.advantages.map(adv => `- ${adv}`).join('\n')}\n\n`;
        }

        if (approach.bestFor) {
          approachesSection += `**Best For:** ${approach.bestFor}\n`;
        }
      });

      if (integration.recommendedApproach) {
        approachesSection += `\n**Recommended Approach:** ${integration.recommendedApproach}\n`;
      }

      // Insert after "What We Are Going to Implement" or after Overview
      const whatMatch = content.match(/(##\s+What\s+We\s+Are\s+Going\s+to\s+Implement[\s\S]*?)(?=\n##|$)/i);
      if (whatMatch) {
        const endOfWhat = content.indexOf(whatMatch[0]) + whatMatch[0].length;
        content = content.slice(0, endOfWhat) + approachesSection + content.slice(endOfWhat);
      } else {
        const overviewMatch = content.match(/(##\s+Overview[\s\S]*?)(?=\n##|$)/i);
        if (overviewMatch) {
          const endOfOverview = content.indexOf(overviewMatch[0]) + overviewMatch[0].length;
          content = content.slice(0, endOfOverview) + approachesSection + content.slice(endOfOverview);
        } else {
          content = approachesSection + '\n\n' + content;
        }
      }
    }

    // Check if endpoints section is missing from raw content
    const hasEndpointsHeader = /##\s+(?:API\s+)?Endpoints:/i.test(content);

    // If we have parsed endpoints but no endpoints section in content, add it
    if (integration.endpoints && integration.endpoints.length > 0 && !hasEndpointsHeader) {
      // Find a good place to insert endpoints (after Implementation Approaches or Overview)
      const approachesMatch = content.match(/(##\s+Implementation\s+Approaches[\s\S]*?)(?=\n##|$)/i);

      if (approachesMatch) {
        const endOfApproaches = content.indexOf(approachesMatch[0]) + approachesMatch[0].length;
        const endpointsSection = `\n\n## API Endpoints\n${integration.endpoints.map(url => `- ${url}`).join('\n')}`;
        content = content.slice(0, endOfApproaches) + endpointsSection + content.slice(endOfApproaches);
      } else {
        const overviewMatch = content.match(/(##\s+Overview[\s\S]*?)(?=\n##|$)/i);
        if (overviewMatch) {
          const endOfOverview = content.indexOf(overviewMatch[0]) + overviewMatch[0].length;
          const endpointsSection = `\n\n## API Endpoints\n${integration.endpoints.map(url => `- ${url}`).join('\n')}`;
          content = content.slice(0, endOfOverview) + endpointsSection + content.slice(endOfOverview);
        } else {
          const endpointsSection = `## API Endpoints\n${integration.endpoints.map(url => `- ${url}`).join('\n')}\n\n`;
          content = endpointsSection + content;
        }
      }
    }

    // Check if configuration section is missing
    const hasConfigHeader = /##\s+Configuration:/i.test(content);
    if (Object.keys(integration.configuration || {}).length > 0 && !hasConfigHeader) {
      const configSection = `\n\n## Configuration\n${Object.entries(integration.configuration).map(([key, value]) => `- **${key}**: ${value}`).join('\n')}`;
      content += configSection;
    }

    return content.trim();
  };

  // CRUD operations
  const handleStartEdit = (integrationId, integration) => {
    setEditingIntegration(integrationId);
    const fullContent = reconstructEditableContent(integration);
    setEditedContent(fullContent);
  };

  const handleCancelEdit = () => {
    setEditingIntegration(null);
    setEditedContent('');
  };

  // Helper function to auto-format plain text to markdown
  const autoFormatToMarkdown = (content) => {
    const lines = content.split('\n');
    const formattedLines = [];
    let inApproachesSection = false;

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) {
        formattedLines.push(line);
        continue;
      }

      // Track if we're in Implementation Approaches section to avoid auto-formatting
      if (/^##\s+Implementation\s+Approaches/i.test(trimmedLine)) {
        inApproachesSection = true;
        formattedLines.push(line);
        continue;
      }

      // Exit Implementation Approaches section when hitting next ## header
      if (inApproachesSection && /^##\s+[^#]/i.test(trimmedLine)) {
        inApproachesSection = false;
      }

      // Skip ALL lines that already have markdown formatting or are in special sections
      if (trimmedLine.startsWith('#') ||
          trimmedLine.startsWith('-') ||
          trimmedLine.startsWith('*') ||
          trimmedLine.startsWith('**') ||
          trimmedLine.match(/^\d+\./) ||
          inApproachesSection) {
        formattedLines.push(line);
        continue;
      }

      // Check if line is under a list section (What We Are Going to Implement, Code Requirements, Advantages)
      // But NOT in Implementation Approaches section
      const previousLines = formattedLines.slice(-10).join('\n').toLowerCase();
      const isInListSection =
        /what we are going to implement/.test(previousLines) ||
        (/code requirements:/.test(previousLines) && !inApproachesSection) ||
        (/advantages:/.test(previousLines) && !inApproachesSection);

      // Check if previous non-empty line was a list item
      let previousLineWasList = false;
      for (let j = i - 1; j >= 0; j--) {
        const prevTrimmed = lines[j].trim();
        if (prevTrimmed) {
          previousLineWasList = prevTrimmed.startsWith('-') || prevTrimmed.startsWith('*');
          break;
        }
      }

      // Auto-format line ONLY if in simple list sections
      let formattedLine = line;

      // Auto-add bullet point if in list context or previous line was a list
      // But NOT in Implementation Approaches section
      if ((isInListSection || previousLineWasList) && trimmedLine.length > 0 && !inApproachesSection) {
        const indent = line.match(/^\s*/)[0];

        // Auto-bold pattern: "Label (CODE123):" or "Label:" at start of line
        let lineContent = trimmedLine;
        const boldPattern = /^([^:]+?)(\s*\([A-Z0-9]+\))?\s*:\s*(.+)$/;
        const boldMatch = lineContent.match(boldPattern);

        if (boldMatch && !lineContent.includes('**')) {
          // Make the label (and code if present) bold
          const label = boldMatch[1];
          const code = boldMatch[2] || '';
          const rest = boldMatch[3];
          lineContent = `**${label}${code}**: ${rest}`;
        }

        formattedLine = `${indent}- ${lineContent}`;
      } else {
        formattedLine = line;
      }

      formattedLines.push(formattedLine);
    }

    return formattedLines.join('\n');
  };

  const handleSaveEdit = async (integrationId) => {
    setIsSaving(true);

    // Store original edited content before clearing state (for error recovery)
    const originalContent = editedContent;

    // Auto-format plain text to markdown
    let contentToSave = autoFormatToMarkdown(originalContent.trim());

    // Remove "Integration N: Title" format only if it's at the very start
    contentToSave = contentToSave.replace(/^Integration\s+\d+:\s*[^\n]+\n*/i, '');

    // Remove the integration title as a ## header only if it's the FIRST line
    // But preserve all other ## headers (section headers)
    const lines = contentToSave.split('\n');
    if (lines.length > 0 && /^##\s+[^#]/.test(lines[0])) {
      // First line is a ## header (not ### or ####), likely the integration title
      // Check if this might be a section header by looking for common section names
      const firstLine = lines[0].toLowerCase();
      const isSectionHeader = /##\s+(overview|what we are going|implementation|approach|endpoint|configuration|authentication|documentation|error|rate|request)/i.test(firstLine);

      if (!isSectionHeader) {
        // It's likely the integration title, remove it
        lines.shift();
        contentToSave = lines.join('\n').trim();
      }
    }

    try {
      // Close edit mode first
      setEditingIntegration(null);
      setEditedContent('');

      // Re-parse the content to extract structured data (options, pros/cons, etc.)
      const reparsedDetails = parseIntegrationDetails(contentToSave);

      const updatedIntegrations = parsedIntegrations.map(int =>
        int.id === integrationId ? {
          ...int,
          rawContent: contentToSave,
          // Update all parsed fields with re-parsed data
          ...reparsedDetails
        } : int
      );

      // Set flag to prevent useEffect from overwriting
      isLocalUpdateRef.current = true;

      // Force immediate state update with new object reference to trigger re-render
      setParsedIntegrations([...updatedIntegrations]);

      const updatedContent = reconstructIntegrationMarkdown(updatedIntegrations);
      contentRef.current = updatedContent;

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          integrations: updatedIntegrations
        });
      }

      // Find and update the message
      let updatedMessages;

      if (currentVersion && currentVersion.messageIndex !== undefined) {
        // If we have version management, use the specific message index
        updatedMessages = conversation.messages.map((msg, index) => {
          if (index === currentVersion.messageIndex) {
            return {
              ...msg,
              content: updatedContent
            };
          }
          return msg;
        });
      } else {
        // No version management - update the last assistant message (single version case)
        updatedMessages = [...conversation.messages];
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update local conversation object to reflect changes immediately
      if (conversation.messages) {
        conversation.messages = updatedMessages;
      }

      // Trigger conversation refresh if callback provided
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Reset the local update flag AFTER all operations complete
      // Use setTimeout to ensure state has fully propagated
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 100);

      showNotification({
        message: 'Integration updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving integration:', error);
      // Reset the local update flag on error too
      isLocalUpdateRef.current = false;
      // Restore edit state on error
      setEditingIntegration(integrationId);
      setEditedContent(contentToSave);
      showNotification({
        message: 'Failed to update integration',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteIntegration = (integration) => {
    setDeleteConfirmation({ open: true, integration });
  };

  const handleDeleteConfirmed = async () => {
    const integration = deleteConfirmation.integration;
    setDeleteConfirmation({ open: false, integration: null });

    if (!integration) return;

    try {
      const updatedIntegrations = parsedIntegrations.filter(int => int.id !== integration.id);

      isLocalUpdateRef.current = true;
      setParsedIntegrations(updatedIntegrations);

      setExpandedIntegrations(prev => {
        const updated = { ...prev };
        delete updated[integration.id];
        return updated;
      });

      const updatedContent = reconstructIntegrationMarkdown(updatedIntegrations);
      contentRef.current = updatedContent;

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          integrations: updatedIntegrations
        });
      }

      // Find and update the message
      let updatedMessages;

      if (currentVersion && currentVersion.messageIndex !== undefined) {
        // If we have version management, use the specific message index
        updatedMessages = conversation.messages.map((msg, index) => {
          if (index === currentVersion.messageIndex) {
            return {
              ...msg,
              content: updatedContent
            };
          }
          return msg;
        });
      } else {
        // No version management - update the last assistant message (single version case)
        updatedMessages = [...conversation.messages];
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update local conversation object to reflect changes immediately
      if (conversation.messages) {
        conversation.messages = updatedMessages;
      }

      // Trigger conversation refresh if callback provided
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Reset the local update flag AFTER all operations complete
      isLocalUpdateRef.current = false;

      showNotification({
        message: `"${integration.title}" deleted successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting integration:', error);
      // Reset the local update flag on error too
      isLocalUpdateRef.current = false;
      showNotification({
        message: `Failed to delete "${integration.title}"`,
        type: 'error'
      });
    }
  };

  const handleCopyIntegration = (integration) => {
    const text = formatIntegrationForCopy(integration);
    navigator.clipboard.writeText(text).then(() => {
      showNotification({
        message: `"${integration.title}" copied to clipboard`,
        type: 'success'
      });
    });
  };

  // Handle adding new integration
  const handleAddIntegration = async (newSection) => {
    try {
      // Create a new integration object
      const newIntegration = {
        id: `integration-${Date.now()}`,
        title: newSection.title,
        rawContent: newSection.content,
        // Parse the content to extract structured data
        ...parseIntegrationDetails(newSection.content),
        authType: null,
        endpoints: [],
        configuration: {},
        implementation: '',
        documentation: ''
      };

      // Set flag to prevent useEffect from overwriting
      isLocalUpdateRef.current = true;

      // Add to existing integrations
      const updatedIntegrations = [...parsedIntegrations, newIntegration];
      setParsedIntegrations(updatedIntegrations);

      // Expand the new integration
      setExpandedIntegrations(prev => ({
        ...prev,
        [newIntegration.id]: true
      }));

      // Reconstruct markdown with all integrations
      const updatedContent = reconstructIntegrationMarkdown(updatedIntegrations);
      contentRef.current = updatedContent;

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          integrations: updatedIntegrations
        });
      }

      // Find and update the message
      let updatedMessages;

      if (currentVersion && currentVersion.messageIndex !== undefined) {
        updatedMessages = conversation.messages.map((msg, index) => {
          if (index === currentVersion.messageIndex) {
            return {
              ...msg,
              content: updatedContent
            };
          }
          return msg;
        });
      } else {
        // No version management - update the last assistant message
        updatedMessages = [...conversation.messages];
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update local conversation object
      if (conversation.messages) {
        conversation.messages = updatedMessages;
      }

      // Trigger conversation refresh
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Reset the local update flag
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 100);

      // Scroll to the new integration after a brief delay to allow rendering
      setTimeout(() => {
        const element = document.getElementById(`accordion-${newIntegration.id}`);
        if (element && integrationPanelRef.current) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);

      showNotification({
        message: `"${newSection.title}" added successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error adding integration:', error);
      isLocalUpdateRef.current = false;
      showNotification({
        message: 'Failed to add integration',
        type: 'error'
      });
      throw error;
    }
  };

  const handleExplainIntegration = (integration) => {
    const formattedIntegration = {
      title: integration.title,
      content: integration.rawContent,
      rawContent: integration.rawContent
    };
    requestExplanation(formattedIntegration);
  };

  // Handle improve
  const handleImproveIntegration = (integration) => {
    const integrationWithContent = {
      ...integration,
      content: integration.rawContent || '',
      rawContent: integration.rawContent || '',
      type: 'integration'
    };
    handleImproveClick(integrationWithContent);

    // Auto-scroll to the improve input after a brief delay to allow rendering
    setTimeout(() => {
      const improveInputElement = improveInputRefs.current[integration.id];
      if (improveInputElement && integrationPanelRef.current) {
        improveInputElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveIntegration || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveIntegration.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveIntegration.title}`, type: 'success' });
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }

      let contentToUse;

      // If improving a specific section, we need to merge it back into full content
      if (isSpecificSectionImprovement && specificSectionName) {
        console.log('🔧 IMPROVE DEBUG - Section Improvement Detected');
        console.log('📌 Section Name:', specificSectionName);

        // Get the current full content of the integration
        const currentIntegration = parsedIntegrations.find(int => int.id === improveIntegration.id);
        const fullContent = currentIntegration?.rawContent || '';

        console.log('📄 Full Content Length:', fullContent.length);
        console.log('📄 Full Content (first 500 chars):', fullContent.substring(0, 500));
        console.log('✨ Improved Content Length:', improvedContent.length);
        console.log('✨ Improved Content (first 500 chars):', improvedContent.substring(0, 500));
        console.log('📦 Merged Content from hook:', improveIntegration.mergedContent?.substring(0, 500));

        // Check if this is a text fragment improvement
        if (specificSectionName === 'text fragment') {
          console.log('📝 TEXT FRAGMENT IMPROVEMENT');
          console.log('✅ Using mergedContent directly (text already replaced in hook)');
          contentToUse = improveIntegration.mergedContent || fullContent;
          console.log('📦 Final Content Length:', contentToUse.length);
        } else {
          // Determine if this is a subsection (###) or main section (##)
          const isSubsection = /^(Approach\s+\d+|Option\s+\d+|\d+\.)/i.test(specificSectionName);
        console.log('🔍 Is Subsection (###)?', isSubsection);

        const sectionName = specificSectionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        console.log('🔍 Escaped Section Name:', sectionName);

        if (isSubsection) {
          // For subsections, use mergedContent which has header + body
          const contentWithHeader = improveIntegration.mergedContent || improvedContent;
          console.log('🔄 Using content with header (first 300 chars):', contentWithHeader.substring(0, 300));

          // Try to find subsection with ###
          const subsectionRegex = new RegExp(
            `(###\\s+${sectionName}[\\s\\S]*?)(?=\\n###\\s+|\\n##\\s+|$)`,
            'i'
          );

          console.log('🔎 Subsection Regex:', subsectionRegex);
          const match = fullContent.match(subsectionRegex);
          console.log('✓ Regex Match Found?', !!match);

          if (match) {
            console.log('✅ Using regex replacement');
            console.log('📝 Matched Content (first 300 chars):', match[0].substring(0, 300));
            contentToUse = fullContent.replace(subsectionRegex, contentWithHeader.trim());
            console.log('📦 Result Content Length:', contentToUse.length);
          } else {
            console.log('⚠️ Regex failed, trying manual search');
            // Try manual search
            const subsectionHeaderPattern = new RegExp(`###\\s+${sectionName}`, 'i');
            const headerIndex = fullContent.search(subsectionHeaderPattern);
            console.log('📍 Header Index:', headerIndex);

            if (headerIndex !== -1) {
              const afterHeader = fullContent.substring(headerIndex);
              const nextSubsectionMatch = afterHeader.substring(1).match(/\n###\s+/);
              const nextSectionMatch = afterHeader.substring(1).match(/\n##\s+[^#]/);

              console.log('⏭️ Next Subsection Found?', !!nextSubsectionMatch);
              console.log('⏭️ Next Section Found?', !!nextSectionMatch);

              let sectionEnd;
              if (nextSubsectionMatch && nextSectionMatch) {
                sectionEnd = headerIndex + Math.min(nextSubsectionMatch.index + 1, nextSectionMatch.index + 1);
              } else if (nextSubsectionMatch) {
                sectionEnd = headerIndex + nextSubsectionMatch.index + 1;
              } else if (nextSectionMatch) {
                sectionEnd = headerIndex + nextSectionMatch.index + 1;
              } else {
                sectionEnd = fullContent.length;
              }

              console.log('📏 Section End Index:', sectionEnd);

              const before = fullContent.substring(0, headerIndex);
              const after = fullContent.substring(sectionEnd);
              console.log('✂️ Before Length:', before.length);
              console.log('✂️ After Length:', after.length);

              contentToUse = before + contentWithHeader.trim() + '\n\n' + after;
              console.log('📦 Final Content Length:', contentToUse.length);
            } else {
              console.warn('⚠️ Could not find subsection header, preserving original content');
              contentToUse = fullContent;
            }
          }
        } else {
          console.log('📄 Main Section (##) Improvement');
          // Looking for main section with ##
          const sectionRegex = new RegExp(
            `(##\\s+${sectionName}[\\s\\S]*?)(?=\\n##\\s+[^#]|$)`,
            'i'
          );

          const match = fullContent.match(sectionRegex);
          if (match) {
            contentToUse = fullContent.replace(sectionRegex, improvedContent.trim());
          } else {
            const headerPattern = new RegExp(`##\\s+${sectionName}`, 'i');
            const headerIndex = fullContent.search(headerPattern);

            if (headerIndex !== -1) {
              const afterHeader = fullContent.substring(headerIndex);
              const nextSectionMatch = afterHeader.substring(1).match(/\n##\s+[^#]/);
              const sectionEnd = nextSectionMatch
                ? headerIndex + nextSectionMatch.index + 1
                : fullContent.length;

              const before = fullContent.substring(0, headerIndex);
              const after = fullContent.substring(sectionEnd);
              // For main sections, just use improvedContent (no header preservation needed)
              contentToUse = before + improvedContent.trim() + '\n\n' + after;
            } else {
              console.warn('⚠️ Could not find section, preserving original content');
              contentToUse = fullContent;
            }
          }
        }
        }
      } else {
        // Full integration improvement
        console.log('🌟 FULL INTEGRATION IMPROVEMENT (Not a specific section)');
        contentToUse = improveIntegration.mergedContent || improvedContent;
        console.log('📦 Using Full Content Length:', contentToUse.length);
      }

      console.log('✅ FINAL contentToUse Length:', contentToUse.length);
      console.log('📄 FINAL contentToUse (first 500 chars):', contentToUse.substring(0, 500));

      // Only strip the main integration title header, preserve section headers
      contentToUse = contentToUse.trim();

      // Remove "Integration N: Title" format only if it's at the very start
      contentToUse = contentToUse.replace(/^Integration\s+\d+:\s*[^\n]+\n*/i, '');

      // Remove the integration title as a ## header only if it's the FIRST line
      const lines = contentToUse.split('\n');
      if (lines.length > 0 && /^##\s+[^#]/.test(lines[0])) {
        const firstLine = lines[0].toLowerCase();
        const isSectionHeader = /##\s+(overview|what we are going|implementation|approach|endpoint|configuration|authentication|documentation|error|rate|request)/i.test(firstLine);

        if (!isSectionHeader) {
          lines.shift();
          contentToUse = lines.join('\n').trim();
        }
      }

      // Update the integration with improved content
      const updatedIntegrations = [...parsedIntegrations].map(int => {
        if (int.id === improveIntegration.id) {
          const reparsedDetails = parseIntegrationDetails(contentToUse);

          return {
            ...int,
            rawContent: contentToUse,
            ...reparsedDetails
          };
        }
        return int;
      });

      // Set flag to prevent useEffect from overwriting
      isLocalUpdateRef.current = true;

      // Update state
      setParsedIntegrations([...updatedIntegrations]);

      // Reconstruct markdown
      const updatedContent = reconstructIntegrationMarkdown(updatedIntegrations);
      contentRef.current = updatedContent;

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          integrations: updatedIntegrations
        });
      }

      // Find and update the message in conversation
      let updatedMessages;

      if (currentVersion && currentVersion.messageIndex !== undefined) {
        updatedMessages = conversation.messages.map((msg, index) => {
          if (index === currentVersion.messageIndex) {
            return {
              ...msg,
              content: updatedContent
            };
          }
          return msg;
        });
      } else {
        updatedMessages = [...conversation.messages];
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || []
        }))
      };

      // Save to backend
      await conversationService.saveConversation(conversation._id, conversationData);

      // Reset the local update flag after save
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 100);

      // Trigger conversation update callback
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      showNotification({
        message: 'Integration improved successfully',
        type: 'success'
      });

      closeImprovementModal();
    } catch (error) {
      console.error('Error updating improved integration:', error);
      showNotification({
        message: 'Failed to update integration',
        type: 'error'
      });
    }
  };

  // Auto-scroll integration panel to bottom when streaming
  useEffect(() => {
    if (isStreaming && integrationPanelRef.current && isIntegrationConversation) {
      setTimeout(() => {
        if (integrationPanelRef.current) {
          integrationPanelRef.current.scrollTop = integrationPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isIntegrationConversation]);

  // Don't show the split view if not integration conversation or no integrations found
  if (!isIntegrationConversation || (!showIntegrationPanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Integration accordion panel */}
        <Box
          ref={integrationPanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Integrations' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={setCurrentVersionIndex}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddIntegrationModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportIntegrations}
                  isExporting={isExporting}
                  onCopy={handleCopyIntegrations}
                  disabled={!contentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add integrations" : "Add new integration"}
                  exportTooltip="Export integrations as markdown"
                  copyTooltip="Copy integrations to clipboard"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'integration'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedIntegrations}
              totalItems={parsedIntegrations?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Show parsed integrations or a placeholder */}
            {isStreaming ? (
              <LoadingBox message='Generating integration documentation...' spinnerSize={40} />
            ) : showIntegrationPanel && parsedIntegrations.length > 0 ? (
              <Box sx={{
                p: 3,
                pt: 2,
                flex: 1,
                overflow: "auto",
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
              }}>
                {/* Render integrations as accordions */}
                {parsedIntegrations.slice(0, visibleIntegrations).map((integration, index) => {
                  const integrationId = integration.id;
                  const versionSpecificId = `${integrationId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                  const isValidated = validatedIntegrations[versionSpecificId] || false;

                  return (
                    <Accordion
                      key={integrationId}
                      id={`accordion-${integrationId}`}
                      ref={(el) => registerSectionRef(integrationId, el)}
                      title={`${index + 1}. ${integration.title}`}
                      defaultExpanded={false}
                      expanded={expandedIntegrations[integrationId]}
                      onChange={(_, expanded) => handleIntegrationToggle(integrationId)}
                      rightIcon={
                        <Box
                          onClick={async (e) => {
                            e.stopPropagation();

                            try {
                              await handleValidateIntegration(integrationId);
                            } catch (error) {
                              showNotification({
                                message: `Failed to update validation for "${integration.title}"`,
                                type: "error"
                              });
                            }
                          }}
                          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                        >
                          {isValidated ? (
                            <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                          ) : (
                            <UnvalidatedIcon size={25} />
                          )}
                        </Box>
                      }
                    >
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        width: '100%',
                        pr: 7, // Right padding to prevent overlap with action buttons
                        minHeight: "160px",
                        cursor: editingIntegration === integrationId ? 'default' : 'pointer',
                      }}>
                        {editingIntegration === integrationId ? (
                          <Box onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ mb: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                              <Typography variant="caption" sx={{ color: '#1565c0', fontSize: '12px', display: 'block', lineHeight: 1.4 }}>
                                💡 <strong>Smart Formatting:</strong> Paste plain text without markdown!<br/>
                                • Bullet points (-, **) added automatically<br/>
                                • Text like "Feature Name: description" becomes <strong>bold</strong> automatically<br/>
                                Example: "SMS Alert (US019): Send notification" → "- <strong>SMS Alert (US019)</strong>: Send notification"
                              </Typography>
                            </Box>
                            <EditableTextField
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                              placeholder="Edit content here... Plain text is automatically formatted on save."
                            />
                          </Box>
                        ) : (
                          <Box>
                            {/* Auth Type Badge */}
                            {integration.authType && (
                              <Box sx={{ mb: 2 }}>
                                <Chip
                                  label={formatAuthType(integration.authType)}
                                  color={getAuthTypeBadgeColor(integration.authType)}
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </Box>
                            )}

                            {/* Full Documentation - Clean Markdown View */}
                            {integration.rawContent && (
                              <Box sx={{
                                mb: 2,
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                  marginTop: '16px',
                                  marginBottom: '8px',
                                  fontWeight: 'bold',
                                  lineHeight: 1.4,
                                },
                                '& h2': { fontSize: '1.25rem' },
                                '& h3': { fontSize: '1.1rem' },
                                '& p': {
                                  marginBottom: '12px',
                                  lineHeight: 1.8,
                                  fontSize: '13px',
                                },
                                '& ul, & ol': {
                                  marginBottom: '12px',
                                  paddingLeft: '24px',
                                  lineHeight: 1.8,
                                },
                                '& li': {
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                },
                                '& code': {
                                  backgroundColor: '#f5f5f5',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '12px',
                                  fontFamily: 'monospace',
                                },
                                '& pre': {
                                  backgroundColor: '#f5f5f5',
                                  padding: '12px',
                                  borderRadius: '4px',
                                  overflow: 'auto',
                                  marginBottom: '12px',
                                },
                                '& pre code': {
                                  backgroundColor: 'transparent',
                                  padding: 0,
                                },
                                '& strong': {
                                  fontWeight: 600,
                                },
                                '& a': {
                                  color: '#1976d2',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                },
                                '& blockquote': {
                                  borderLeft: '4px solid #ddd',
                                  paddingLeft: '16px',
                                  marginLeft: 0,
                                  fontStyle: 'italic',
                                  color: '#666',
                                },
                              }}>
                                <ReactMarkdown>
                                  {integration.rawContent}
                                </ReactMarkdown>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Inline improve input */}
                        {showImproveInput === integrationId && (
                          <InlineImproveInput
                            ref={(el) => {
                              if (el) {
                                improveInputRefs.current[integrationId] = el;
                              }
                            }}
                            improvePrompt={improvePrompt}
                            onPromptChange={setImprovePrompt}
                            onSend={() => {
                              const integrationWithContent = {
                                ...integration,
                                content: integration.rawContent || '',
                                rawContent: integration.rawContent || '',
                                type: 'integration'
                              };
                              handleSendImprove(integrationWithContent, improvePrompt);
                            }}
                            onCancel={handleCancelImprove}
                          />
                        )}
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{
                        position: 'absolute',
                        right: 10,
                        top: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5
                      }}>
                        <ActionButtons
                          item={integration}
                          itemIndex={index}
                          featureId={integrationId}
                          editingFeature={editingIntegration}
                          isSaving={isSaving}
                          onEdit={() => handleStartEdit(integrationId, integration)}
                          onSave={() => handleSaveEdit(integrationId)}
                          onCancel={handleCancelEdit}
                          onImprove={() => handleImproveIntegration(integration)}
                          onExplain={() => handleExplainIntegration(integration)}
                          onCopy={() => handleCopyIntegration(integration)}
                          onDelete={() => handleDeleteIntegration(integration)}
                          vertical={true}
                          onComment={() => openCommentPanel(integrationId)}
                          commentCount={commentCounts[integrationId]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                        />
                      </Box>
                    </Accordion>
                  );
                })}

                {/* Load more button */}
                {parsedIntegrations.length > visibleIntegrations && (
                  <LoadMoreButton
                    remaining={parsedIntegrations.length - visibleIntegrations}
                    onLoadMore={handleLoadMore}
                    onShowAll={handleShowAll}
                    label="Load more"
                  />
                )}
              </Box>
            ) : (
              <EmptyStateBox message="No integration documentation found in this conversation yet." />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, integration: null })}
        title="Delete integration"
        message={`Are you sure you want to delete "${deleteConfirmation.integration?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirmed}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainIntegration?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveIntegration}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddIntegrationModal}
        onClose={() => setShowAddIntegrationModal(false)}
        onSave={handleAddIntegration}
        sectionType="integration"
        existingSections={parsedIntegrations}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedIntegrations || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewIntegration;
